import { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Alert, Chip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDias } from '../../utils/formatters';
import { situacaoConfig, textoDoErro } from './contratosNovosUtils';
import { PlanejamentoNoCadastro, mesesEntre, paraLista, primeiroErro } from '../PlanejamentoCarga';

const FORM_VAZIO = {
  nome: '', celula: '', membros: [], data_inicio: '', data_fim: '', escopo: '', sponsor: '', coordenador_corporativo: '',
  planejamento: {},          // carga planejada mês a mês: { '2026-10': { consultoria: '12', workshop: '2' } }
};

// Tela Projetos: cada contrato cadastrado pelo Comercial é um grupo com os projetos dele embaixo. Quem inicia
// o projeto adiciona os projetos do contrato e define a célula (vale para o contrato inteiro) e os consultores.
export default function ContratosProjetos({ contratos, projetos, busca = '', renderProjeto, aoAdicionarProjeto }) {
  const { temAcesso } = useAuth();
  const podeAdicionar = temAcesso('iniciar_projeto');

  const [opcoes, setOpcoes] = useState({ celulas: [], membros: [] });
  const [sucesso, setSucesso] = useState('');
  const [erro, setErro] = useState('');

  const [contrato, setContrato] = useState(null);         // contrato que vai receber o projeto
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');

  const abrir = async (c) => {
    setContrato(c);
    setForm({ ...FORM_VAZIO, celula: c.celula || '' });
    setErroForm('');
    try {
      const res = await api.get('/contratos/opcoes-projeto/');
      setOpcoes(res.data);
    } catch (err) {
      setErroForm(textoDoErro(err, 'Não foi possível carregar as células e os consultores.'));
    }
  };

  const celulaDefinida = Boolean(contrato?.celula);

  const salvar = async () => {
    setErroForm('');
    if (!form.nome.trim()) { setErroForm('O nome do projeto é obrigatório.'); return; }
    if (!celulaDefinida && !form.celula) { setErroForm('Escolha a célula do contrato (vale para todos os projetos dele).'); return; }
    if (form.data_inicio && form.data_fim && form.data_fim < form.data_inicio) { setErroForm('A data fim não pode ser antes da data início.'); return; }
    // Planejamento da carga: só os meses entre a data início e a data fim.
    const mesesDoProjeto = mesesEntre(form.data_inicio, form.data_fim);
    const erroPlano = primeiroErro(mesesDoProjeto, form.planejamento);
    if (erroPlano) { setErroForm(`Planejamento da carga — ${erroPlano}`); return; }
    const payload = { nome: form.nome.trim(), membros_equipe: form.membros.map((m) => m.id) };
    const planejamento = paraLista(mesesDoProjeto, form.planejamento).filter((i) => i.consultoria || i.workshop);
    if (planejamento.length > 0) payload.planejamento = planejamento;
    if (!celulaDefinida) payload.celula = form.celula;
    ['data_inicio', 'data_fim', 'escopo', 'sponsor', 'coordenador_corporativo'].forEach((campo) => {
      if (form[campo]) payload[campo] = form[campo];
    });
    setSalvando(true);
    try {
      await api.post(`/contratos/${contrato.id}/adicionar-projeto/`, payload);
      setSucesso(`Projeto "${form.nome.trim()}" adicionado ao contrato "${contrato.nome}". Ele nasce em Planejamento: use "Iniciar projeto" na tela do projeto quando for começar.`);
      setContrato(null);
      if (aoAdicionarProjeto) aoAdicionarProjeto();
    } catch (err) {
      setErroForm(textoDoErro(err, 'Erro ao adicionar o projeto.'));
    } finally {
      setSalvando(false);
    }
  };

  const termo = busca.trim().toLowerCase();
  const doContrato = (c) => projetos.filter((p) => p.contrato_novo_id === c.id);
  const visiveis = contratos.filter((c) => (
    !termo
    || c.nome?.toLowerCase().includes(termo)
    || c.cliente_nome?.toLowerCase().includes(termo)
    || doContrato(c).length > 0
  ));
  const aguardando = contratos.filter((c) => c.situacao === 'aguardando_projeto').length;

  return (
    <Box sx={{ mb: 3 }}>
      {aguardando > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip size="small" color="warning" label={`${aguardando} ${aguardando === 1 ? 'contrato aguardando projeto' : 'contratos aguardando projeto'}`} />
        </Box>
      )}
      {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}
      {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}

      {visiveis.map((c) => {
        const st = situacaoConfig[c.situacao] || situacaoConfig.aguardando_projeto;
        const semProjeto = c.situacao === 'aguardando_projeto';
        const lista = doContrato(c);
        return (
          <Card
            key={c.id}
            sx={{ borderRadius: 3, mb: 3, border: '1px solid #e5e7eb', boxShadow: 'none', backgroundColor: semProjeto ? '#FFFBF3' : '#f8f9fa' }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }}>{c.nome}</Typography>
                    <Chip size="small" label={semProjeto ? st.label : `${c.projetos.length} ${c.projetos.length === 1 ? 'projeto' : 'projetos'}`} color={st.color} />
                  </Box>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {c.cliente_nome} · Contrato com {formatDias(c.carga_total_contratada)} de carga e {c.workshops_vendidos ?? 0} {(c.workshops_vendidos ?? 0) === 1 ? 'workshop' : 'workshops'}
                    {' · '}{c.celula_nome ? `Célula: ${c.celula_nome}` : 'célula ainda não definida'}
                  </Typography>
                </Box>
                {podeAdicionar && (
                  <Button
                    size="small" variant={semProjeto ? 'contained' : 'outlined'}
                    startIcon={<AddIcon />} onClick={() => abrir(c)}
                    sx={semProjeto ? { backgroundColor: '#13375F', textTransform: 'none' } : { textTransform: 'none' }}
                  >
                    Adicionar projeto
                  </Button>
                )}
              </Box>
              {lista.length === 0 ? (
                <Typography variant="body2" sx={{ color: semProjeto ? '#BA7517' : '#64748b' }}>
                  {semProjeto
                    ? 'Este contrato ainda não tem projeto.'
                    : 'Nenhum projeto deste contrato corresponde à busca.'}
                  {semProjeto && podeAdicionar ? ' Clique em Adicionar projeto para começar.' : ''}
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {lista.map((p) => renderProjeto(p))}
                </Grid>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Dialog open={Boolean(contrato)} onClose={salvando ? undefined : () => setContrato(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Adicionar projeto</DialogTitle>
        <Typography variant="body2" color="text.secondary" sx={{ px: 3, mb: 1 }}>
          Contrato: <b>{contrato?.nome}</b> — {contrato?.cliente_nome}
        </Typography>
        <DialogContent>
          {erroForm && <Alert severity="error" sx={{ mb: 2 }}>{erroForm}</Alert>}
          {celulaDefinida ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Este contrato está na célula <b>{contrato.celula_nome}</b>. Todos os projetos dele ficam nela.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Este é o primeiro projeto do contrato. A célula que você escolher vale para o contrato inteiro,
              incluindo os projetos que forem adicionados depois.
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth required label="Nome do projeto" value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                select fullWidth required={!celulaDefinida} label="Célula do contrato"
                value={form.celula} disabled={celulaDefinida}
                onChange={(e) => setForm({ ...form, celula: e.target.value })}
              >
                {opcoes.celulas.map((cel) => <MenuItem key={cel.id} value={cel.id}>{cel.nome}</MenuItem>)}
                {celulaDefinida && !opcoes.celulas.some((cel) => cel.id === contrato.celula) && (
                  <MenuItem value={contrato.celula}>{contrato.celula_nome}</MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid size={12}>
              <Autocomplete
                multiple
                options={opcoes.membros}
                value={form.membros}
                getOptionLabel={(m) => m.nome || ''}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                groupBy={(m) => m.celula_nome || 'Sem célula'}
                onChange={(e, novo) => setForm({ ...form, membros: novo })}
                renderInput={(params) => (
                  <TextField {...params} label="Consultores do projeto" placeholder="Buscar consultor..." />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth type="date" label="Data início" value={form.data_inicio}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => setForm({ ...form, data_inicio: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth type="date" label="Data fim" value={form.data_fim}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => setForm({ ...form, data_fim: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth label="Escopo (opcional)" value={form.escopo}
                slotProps={{ htmlInput: { maxLength: 300 } }}
                onChange={(e) => setForm({ ...form, escopo: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Sponsor (opcional)" value={form.sponsor}
                onChange={(e) => setForm({ ...form, sponsor: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth label="Coordenador corporativo (opcional)" value={form.coordenador_corporativo}
                onChange={(e) => setForm({ ...form, coordenador_corporativo: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <PlanejamentoNoCadastro
                dataInicio={form.data_inicio} dataFim={form.data_fim} valores={form.planejamento}
                aoMudar={(ano, mes, campo, texto) => setForm((f) => {
                  const chave = `${ano}-${mes}`;
                  return { ...f, planejamento: { ...f.planejamento, [chave]: { ...(f.planejamento[chave] || {}), [campo]: texto } } };
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContrato(null)} disabled={salvando}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={salvando} sx={{ backgroundColor: '#13375F' }}>
            {salvando ? 'Salvando...' : 'Adicionar projeto'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
